import {
  Alert,
  Modal,
  Space,
  Spin,
  Table,
  Typography,
} from "@pankod/refine-antd";
import { useApiUrl, useCustom } from "@pankod/refine-core";
import { FC } from "react";

type SellerCommercialCondition = {
  cdCondicao: string;
  nmCondicao: string;
  dsCondicao: string;
  flTabPadrao?: string | null;
};

type CommercialConditionsModalProps = {
  isOpen: boolean;
  sellerCode: string | null;
  sellerName?: string | null;
  onClose: () => void;
};

const COMMERCIAL_CONDITIONS_RESOURCE = "v1/partner-sellers";

// Retry automático do react-query (padrão: 3 tentativas com backoff) faz sentido só pra
// falha transiente (rede/5xx) — um 404 do Admin.Bff aqui é resposta determinística
// ("vendedor não encontrado no Vendas Direta") e não muda numa segunda tentativa. Sem essa
// checagem, o modal fica minutos exibindo a tabela vazia (parecendo sucesso, com o Spin por
// cima) enquanto tenta de novo em segundo plano, até finalmente cair no estado de erro —
// falso positivo visual reportado em produção.
const shouldRetryCommercialConditionsQuery = (
  failureCount: number,
  error: unknown
): boolean => {
  const status =
    (error as { response?: { status?: number }; statusCode?: number })
      ?.response?.status ??
    (error as { statusCode?: number })?.statusCode;

  if (typeof status === "number" && status < 500) {
    return false;
  }

  return failureCount < 2;
};

export const CommercialConditionsModal: FC<CommercialConditionsModalProps> = ({
  isOpen,
  sellerCode,
  sellerName,
  onClose,
}) => {
  const apiUrl = useApiUrl();

  const { data, isLoading, isError, refetch } = useCustom<
    SellerCommercialCondition[]
  >({
    url: `${apiUrl}/${COMMERCIAL_CONDITIONS_RESOURCE}/${sellerCode ?? ""}/commercial-conditions`,
    method: "get",
    queryOptions: {
      enabled: isOpen && !!sellerCode,
      retry: shouldRetryCommercialConditionsQuery,
    },
  });

  const conditions = data?.data ?? [];

  return (
    <Modal
      visible={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
      title={
        sellerName
          ? `Condições comerciais — ${sellerName}`
          : "Condições comerciais"
      }
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        {isError ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              type="error"
              message="Erro ao consultar condições comerciais do vendedor"
              description="Tente novamente em instantes."
              showIcon
            />
            <Typography.Link onClick={() => refetch()}>
              Tentar de novo
            </Typography.Link>
          </Space>
        ) : (
          <Table
            dataSource={conditions}
            rowKey="cdCondicao"
            pagination={false}
            locale={{
              emptyText: isLoading
                ? " "
                : "Nenhuma condição comercial encontrada para este vendedor",
            }}
          >
            <Table.Column
              dataIndex="cdCondicao"
              key="cdCondicao"
              title="Código"
            />
            <Table.Column
              dataIndex="nmCondicao"
              key="nmCondicao"
              title="Nome"
            />
            <Table.Column
              dataIndex="dsCondicao"
              key="dsCondicao"
              title="Descrição"
            />
          </Table>
        )}
      </Spin>
    </Modal>
  );
};
